import { JsonapiUrlGenerator,
  JsonApiBackend,
  Store,
  PresenterFactory,
  EntityMappingFactory,
  EntityMappingManager,
  ModelIdentityMap,
  ModelMapper,
  EntityMapping,
  IEntityMappingConfig,
  ModelRequestStore,
  RequestKeyStrategy,
  ModelIncludeFactory,
  DeduplicatedFindBackend,
  BackendStrategy
} from '../../backends'
import { AppContext, HttpHelper, Utils, ObservableUtils, TypeOf } from '../../../helpers'
import { YaysonPresenter } from '../../backends/json-api-backend/presenters/yayson.presenter'
import { ModelFactory } from '../../../data/factories'
import { MemoryBackend } from '../../backends/memory-backend/memory.backend'
import { Observable } from 'rxjs'
import { BaseModel } from '../../../models'

export class BackendBuilder {
  protected _entityMappingManager: EntityMappingManager
  protected _cacheBuilderMethod: string
  protected _modelIdentityMap: ModelIdentityMap

  constructor(
    private _apiRoot: string,
    private _appContext: AppContext,
    private _modelClasses: any[],
    private _endpointMapping: {},
    private _entityMappingConfigs: IEntityMappingConfig[],
    private _presenterClasses: Array<TypeOf<YaysonPresenter>>,
    private _httpHelper: HttpHelper,
    private _utils: Utils,
    private _observableUtils: ObservableUtils,
    private _modelIncludeFactory: ModelIncludeFactory,
    private _entityMappingFactory: EntityMappingFactory
  ) {
    this._validateEntityParameters()

    this._entityMappingManager = new EntityMappingManager(
      this._entityMappingConfigs,
      this._entityMappingFactory,
      this._utils
    )
  }

  public withCache() {
    this._cacheBuilderMethod = '_buildCachedBackend'
    return this._returnReadyBuildOptions(this._clone())
  }

  public withoutCache() {
    this._cacheBuilderMethod = '_buildUncachedBackend'
    return this._returnReadyBuildOptions(this._clone())
  }

  private _setModelIdentityMap(modelIdentityMap: ModelIdentityMap) {
    this._modelIdentityMap = modelIdentityMap
    return this._returnReadyBuildOptions(this)
  }

  private _build() {
    const backendFactoryMethod = this[this._cacheBuilderMethod]
    const backend = backendFactoryMethod.call(this, this._modelIdentityMap)
    return backend
  }

  private _validateEntityParameters() {
    const countModelClasses = this._modelClasses.length
    const countEndpointMappings = Object.keys(this._endpointMapping).length
    const countPresenterClasses = this._presenterClasses.length

    if (countEndpointMappings !== countModelClasses) {
      throw new Error('Not all model classes have a configured endpoint!')
    } else if (countPresenterClasses !== countModelClasses) {
      throw new Error(`Not all model classes have presenters!`)
    }
  }

  private _buildCachedBackend(modelIdentityMap: ModelIdentityMap) {
    return new MemoryBackend(
      this._buildUncachedBackend(modelIdentityMap),
      this._createModelRequestStore(),
      this._createKeyStrategy(),
      this._observableUtils,
      this._modelIncludeFactory
    )
  }

  private _buildUncachedBackend(modelIdentityMap: ModelIdentityMap) {
    const jsonapiBackend = new JsonApiBackend(
      this._httpHelper,
      this._appContext,
      this._createStore(),
      this._createPresenterFactory(),
      this._createJsonApiUrlGenerator(),
      this._createModelMapper()
    )
    return this._wrapInDuplicateFindBackend(jsonapiBackend)
  }

  private _wrapInDuplicateFindBackend(backend: BackendStrategy<BaseModel>) {
    return new DeduplicatedFindBackend(
      backend,
      this._createModelRequestStore(),
      this._utils,
      this._modelIncludeFactory
    )
  }

  private _createStore() {
    return new Store()
  }

  private _createModelRequestStore() {
    return new ModelRequestStore<Observable<BaseModel[]>>()
  }

  private _createJsonApiUrlGenerator(): JsonapiUrlGenerator {
   return new JsonapiUrlGenerator(
     this._apiRoot, this._endpointMapping, this._entityMappingManager, this._appContext
   )
 }

  private _createModelMapper(): ModelMapper {
    return new ModelMapper(
      this._createOrGetIdentityMap(),
      this._createModelFactory(),
      this._entityMappingManager,
      this._utils
    )
  }

  private _createKeyStrategy() {
    return new RequestKeyStrategy(this._utils)
  }

  private _createModelFactory() {
    return new ModelFactory(this._modelClasses, this._utils)
  }

  private _createOrGetIdentityMap() {
    return this._modelIdentityMap || new ModelIdentityMap()
  }

  private _createPresenterFactory() {
    const nonNullPresenterClasses = this._presenterClasses.filter((c) => c !== null)
    return new PresenterFactory(nonNullPresenterClasses, this._utils)
  }

  private _clone() {
    const copy = new BackendBuilder(
      this._apiRoot,
      this._appContext,
      this._modelClasses,
      this._endpointMapping,
      this._entityMappingConfigs,
      this._presenterClasses,
      this._httpHelper,
      this._utils,
      this._observableUtils,
      this._modelIncludeFactory,
      this._entityMappingFactory
    )
    copy._cacheBuilderMethod = this._cacheBuilderMethod
    copy._modelIdentityMap = this._modelIdentityMap
    copy._entityMappingManager = this._entityMappingManager
    return copy
  }

  private _returnReadyBuildOptions(backendBuilderInstance: BackendBuilder) {
    return new ReadyBuildOptions(
      backendBuilderInstance._setModelIdentityMap.bind(backendBuilderInstance),
      backendBuilderInstance._build.bind(backendBuilderInstance)
    )
  }
}

class ReadyBuildOptions {
  constructor(
    public setModelIdentityMap: (idMap: ModelIdentityMap) => ReadyBuildOptions,
    public build: () => BackendStrategy<BaseModel>
  ) {}
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/model-repository/creators/backend-builder/backend.builder.ts